import { Button, ButtonProps, Spinner } from '@ag1/ui';
import { UnifiedButtonType } from 'shared/studio/UnifiedButton/UnifiedButton.types';
import clsx, { ClassValue } from 'clsx';
import { usePageContext } from 'processes/Page/PageContext';
import { useMemo } from 'react';
import { CustomButtonFields } from '../types';
import styles from '../../styles.module.scss';

type ContentBlockButtonCustomization = Pick<
  ButtonProps,
  'size' | 'onClick' | 'variant' | 'icon' | 'iconPosition'
> &
  Pick<UnifiedButtonType, 'hoverColor'> & {
    isLoading?: boolean;
    isDisabled?: boolean;
  };

export const ContentBlockButton: React.FC<{
  className?: ClassValue;
  button: Omit<CustomButtonFields, 'link'> & { link?: string | null };
  fluid?: boolean;
  customization?: ContentBlockButtonCustomization;
  ignoreLinkDisable?: boolean;
}> = ({
  className,
  button: { label, link: linkProp },
  fluid,
  customization,
  ignoreLinkDisable
}) => {
  const { shouldDisableCTALink, maybeReplaceOfferlessCheckoutLink } = usePageContext();

  // Transform old custom content build-a-bundle links to new offer links when
  // applicable.
  const link = useMemo(
    () => maybeReplaceOfferlessCheckoutLink(linkProp),
    [linkProp, maybeReplaceOfferlessCheckoutLink]
  );

  const disableLink = useMemo(
    () => !ignoreLinkDisable && shouldDisableCTALink(link, { noEmpty: true }),
    [ignoreLinkDisable, link, shouldDisableCTALink]
  );

  if (customization) {
    const { size, onClick, variant, icon, iconPosition, hoverColor, isLoading, isDisabled } =
      customization;

    const hasAction = !!onClick || !!link;
    const disabled = isLoading || isDisabled || disableLink || !hasAction;

    return (
      <Button
        className={clsx(className, variant === 'primary' && hoverColor === 'dark' && styles.dark)}
        href={link}
        fluid={fluid}
        disabled={disabled}
        rounded
        size={size}
        variant={variant}
        icon={!isLoading && icon}
        iconPosition={iconPosition}
        onClick={disabled ? undefined : onClick}>
        {isLoading ? (
          <Spinner color="brand-white" size={24} withOverlay={false} />
        ) : (
          <span>{label}</span>
        )}
      </Button>
    );
  }
  return (
    <Button
      className={clsx(styles.button, className)}
      href={link}
      fluid={fluid}
      disabled={disableLink}
      rounded>
      {label}
    </Button>
  );
};
